import React, {useCallback, useEffect, useState} from 'react'
import {MBMemberInput} from '../../Atoms/MBMemberInput'
import {PasswordRuleText} from '../../Molecules/PasswordRuleText'
import {MBNgBadge, MBOkBadge, MBRequiredBadge} from '../../Atoms/Badges'
import {Spacer} from '../../../../Common/Components/Spacer'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {MBContentNoteWithMarkTypography, MBContentSubTitleTypography,} from '../../Atoms/Typographies'
import {FlexContainer} from '../../Atoms/Containers'

const maxPasswordLength =
  parseInt(process.env.REACT_APP_MEMBER_PASSWORD_FORM_MAX_LENGTH ?? '64', 10)
const passwordRuleConfig: { text: string, regexp: RegExp, name: string }[] = []
for (let i = 0; ; i++) {
  const env = process.env[`REACT_APP_MEMBER_PASSWORD_RULE_TEXT_${i}`]
  if (!env || env.length <= 0) {
    break
  }
  const val = JSON.parse(env)
  passwordRuleConfig[i] = {
    text: val["text"],
    regexp: new RegExp(val["regexp"]),
    name: val["name"]
  }
}

export type PasswordFormProps = {
  showRequiredBadge: boolean
  password: string
  onChange: (password: string) => void
  passwordErrors: string[]
  defaultShowPasswordChecked: boolean
  onCheckboxChange: (checked: boolean) => void
}

export const PasswordForm: React.VFC<PasswordFormProps> = (props) => {
  const [showPassword, setShowPassword] = useState(false)
  const [passwordValidState, setPasswordValidState] = useState<{ [key: string]: boolean }>(
    passwordRuleConfig.reduce(
      (acc: { [key: string]: boolean }, config) => {
        acc[config.name] = false
        return acc
      }, {}
    )
  )
  const [errorMessageList, setErrorMessageList] = useState<string[]>([])
  const [is64overPassword, setIs64overPassword] = useState(false)
  const [isIncludeUnusableChars, setIsIncludeUnusableChars] = useState(false)

  useEffect(() => {
    const enteredPasswordErrorMessage: string[] = []

    if (props.password.match(/[^0-9a-zA-Z!"#$%&'()*+,./:;<=>?\\[\]^_`{|}~@-]/)) {
      setIsIncludeUnusableChars(true)
      const unusableChars = props.password.replace(
        /[0-9a-zA-Z!"#$%&'()*+,./:;<=>?\\[\]^_`{|}~@-]/g,
        '',
      ).match(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[\s\S]/g) || [] as string[]
      const deduplicatedChars = new Set(unusableChars.map(s => {
        if (s === '\u0020' || s === '\u3000') {
          return '空白文字'
        }
        return s
      }).sort())
      enteredPasswordErrorMessage.push(`『${Array.from(deduplicatedChars).join('』『')}』はパスワードに利用できません。`)
    } else {
      setIsIncludeUnusableChars(false)
    }

    if (props.password.length > maxPasswordLength) {
      setIs64overPassword(true)
      enteredPasswordErrorMessage.push(`パスワードは${maxPasswordLength}文字以内で設定ください。`)
    } else {
      setIs64overPassword(false)
    }

    setErrorMessageList([...props.passwordErrors, ...enteredPasswordErrorMessage])
  }, [props.password, props.passwordErrors])

  useEffect(() => {
    setShowPassword(props.defaultShowPasswordChecked)
  }, [props.defaultShowPasswordChecked])

  const handlePasswordValidate = useCallback((rule: string, isValid: boolean) => {
    setPasswordValidState(prevState => {
      return {
        ...prevState,
        [rule]: isValid,
      }
    })
  }, [])

  return (
    <div data-testid={'mb-password-form'}>
      <FlexContainer alignItems={'center'}>
        <label htmlFor="password-input">
          <MBContentSubTitleTypography>パスワード</MBContentSubTitleTypography>
        </label>
        {props.showRequiredBadge && (
          <Spacer ml={'6px'}>
            <FlexContainer verticalAlign={'middle'}>
              <MBRequiredBadge/>
            </FlexContainer>
          </Spacer>
        )}
      </FlexContainer>

      <Spacer mt={'9px'}>
        <MBMemberInput
          type={showPassword ? 'text' : 'password'}
          autoComplete="off"
          className="form-text_01 w_100"
          value={props.password || ''}
          id="password-input"
          placeholder=""
          hasError={props.passwordErrors.length > 0}
          onChange={(e): void => {
            props.onChange(e.target.value)
          }}
        />
      </Spacer>

      <Spacer mt={'10px'}>
        <label>
          <MBMemberInput
            className="form-check_01"
            type="checkbox"
            checked={showPassword}
            hasError={false}
            onChange={(): void => {
              setShowPassword(!showPassword)
              props.onCheckboxChange(!showPassword)
            }}
          />
          <span className="txt">パスワードを表示する</span>
        </label>
      </Spacer>

      <ErrorDisplay errors={errorMessageList}/>

      <Spacer mt={'12px'}>
        <FlexContainer alignItems={'center'}>
          <MBContentNoteWithMarkTypography note={'パスワードは下記の条件を全て満たす必要があります'}/>
          {getPasswordValidBadge(
            props.password, passwordValidState, isIncludeUnusableChars, is64overPassword,
          )}
        </FlexContainer>
        {passwordRuleConfig.map((config, index) =>
          <PasswordRuleText
            key={index}
            ruleText={config.text}
            password={props.password}
            rule={config.regexp}
            onRuleValidate={(isValid): void => handlePasswordValidate(config.name, isValid)}
          />
        )}
      </Spacer>
    </div>
  )
}

const getPasswordValidBadge = (
  password: string,
  passwordValidState: { [key: string]: boolean },
  isIncludeUnusableChars: boolean,
  is64overPassword: boolean,
): JSX.Element => {
  if (password === '') {
    return <></>
  }

  const isUnavailable = Object.keys(passwordValidState).some((key) => {
    return !passwordValidState[key]
  })

  return (isUnavailable || isIncludeUnusableChars || is64overPassword) ?
    <Spacer ml={'6px'}>
      <FlexContainer verticalAlign={'middle'}>
        <MBNgBadge/>
      </FlexContainer>
    </Spacer> :
    <Spacer ml={'6px'}>
      <FlexContainer verticalAlign={'middle'}>
        <MBOkBadge/>
      </FlexContainer>
    </Spacer>
}
